/**
 * Seo.tsx
 *
 * File purpose:
 * - Central SEO helper component to manage page title, meta tags, Open Graph, canonical links
 *   and JSON-LD structured data injections in a non-invasive way.
 *
 * Notes:
 * - This component mutates the document head (adds/updates meta tags and script[type="application/ld+json"])
 *   in a way that is safe to mount/unmount and cleans up on unmount to avoid duplicates.
 * - No visual UI is rendered to the page body; the component is used purely for head-level SEO data.
 */

import React, { useEffect } from 'react'

/**
 * Props for the Seo component
 * - title: Page title (used to set document.title)
 * - description: Meta description
 * - url: Canonical URL of the page
 * - image: Open Graph / social preview image
 * - jsonLd: Optional array of JSON-LD objects to inject (will be stringified)
 */
export interface SeoProps {
  /** Page title */
  title?: string
  /** Meta description */
  description?: string
  /** Canonical URL */
  url?: string
  /** Social image URL */
  image?: string
  /** Additional JSON-LD objects to inject to head */
  jsonLd?: Array<Record<string, unknown>>
}

/**
 * Default organization structured data for Edicoo
 * - Keep this minimal and factual. It will be included if no custom Organization JSON-LD is passed.
 */
const DEFAULT_ORG_JSONLD: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Edicoo Media and Digital Marketing Agency',
  url: 'https://your-site.com',
  logo: 'https://your-site.com/Edicoo-FINAL.png',
  sameAs: [
    'https://www.linkedin.com/company/edicoo',
    'https://www.facebook.com/edicoo',
    'https://www.instagram.com/edicoo'
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+964-XXXXXXXX',
      contactType: 'customer service',
      areaServed: 'IQ'
    }
  ]
}

/**
 * Internal helper: set or update a meta tag by name or property.
 * - Ensures a single meta tag exists for the given key (name or property)
 * - Returns the element created or updated (for possible cleanup)
 *
 * @param key meta name or property string
 * @param value content value
 * @param isProperty if true uses property attribute instead of name
 */
function setMetaTag(key: string, value: string | undefined, isProperty = false): HTMLMetaElement | null {
  if (!value) return null
  const selector = isProperty ? `meta[property="${key}"]` : `meta[name="${key}"]`
  let el = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    if (isProperty) el.setAttribute('property', key)
    else el.setAttribute('name', key)
    el.setAttribute('data-edicoo-managed', 'true')
    document.head.appendChild(el)
  }
  el.setAttribute('content', value)
  return el
}

/**
 * Internal helper: inject JSON-LD script into head and return the element
 * - Uses a deterministic id to avoid duplicates when possible
 *
 * @param json json object to inject
 * @param idSuffix optional suffix to create a unique id
 */
function injectJsonLd(json: Record<string, unknown>, idSuffix?: string): HTMLScriptElement {
  const id = `edicoo-jsonld-${(json['@type'] as string) || 'item'}${idSuffix ? `-${idSuffix}` : ''}`
  // If an element with same id exists, replace content
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    el.setAttribute('data-edicoo-managed', 'true')
    document.head.appendChild(el)
  }
  el.text = JSON.stringify(json)
  return el
}

/**
 * Seo component
 *
 * Purpose:
 * - Apply page title/meta tags and inject Open Graph/Twitter tags and JSON-LD into the head.
 * - Cleanup is intentionally minimal: elements created have data-edicoo-managed attribute; this component
 *   does not remove existing non-managed tags.
 *
 * Usage:
 * - Put <Seo title="..." description="..." url="..." image="..." jsonLd={[...]} /> at the top level of any page component.
 */
export default function Seo(props: SeoProps): null {
  const { title, description, url, image, jsonLd } = props

  useEffect(() => {
    // Document title
    if (title) {
      const suffix = ' | Edicoo'
      document.title = title.includes('Edicoo') ? title : `${title}${suffix}`
    }

    // Meta description
    const descEl = setMetaTag('description', description)

    // Open Graph
    setMetaTag('og:title', title || document.title, true)
    setMetaTag('og:description', description || '', true)
    if (image) setMetaTag('og:image', image, true)
    if (url) setMetaTag('og:url', url, true)
    setMetaTag('og:type', 'website', true)

    // Twitter Card
    setMetaTag('twitter:card', image ? 'summary_large_image' : 'summary')
    setMetaTag('twitter:title', title || document.title)
    if (description) setMetaTag('twitter:description', description)
    if (image) setMetaTag('twitter:image', image)

    // Canonical link
    if (url) {
      let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.rel = 'canonical'
        canonical.setAttribute('data-edicoo-managed', 'true')
        document.head.appendChild(canonical)
      }
      canonical.href = url
    }

    // Inject default organization JSON-LD (if not provided in jsonLd)
    const providedOrg = (jsonLd || []).some((j) => !!j && (j['@type'] === 'Organization' || (j['@type'] as string)?.toLowerCase?.() === 'organization'))
    if (!providedOrg) {
      injectJsonLd(DEFAULT_ORG_JSONLD, 'organization')
    }

    // Inject user JSON-LD objects
    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((j, i) => {
        try {
          injectJsonLd(j, String(i))
        } catch (e) {
          // Swallow JSON-LD injection errors to avoid breaking the page
          // eslint-disable-next-line no-console
          console.warn('Failed to inject JSON-LD', e)
        }
      })
    }

    // No DOM cleanup here to avoid accidentally removing non-managed tags.
    // Managed elements carry data-edicoo-managed attribute and deterministic ids for JSON-LD.

    return () => {
      // Cleanup: remove meta tags and scripts that were created by this component in this lifecycle.
      // We only remove tags we created (data-edicoo-managed).
      const managed = Array.from(document.head.querySelectorAll('[data-edicoo-managed]'))
      managed.forEach((node) => {
        // Keep Organization JSON-LD (id starts with edicoo-jsonld-Organization) — do not remove to keep site-level presence.
        if (node.id && node.id.startsWith('edicoo-jsonld-Organization')) return
        node.parentNode?.removeChild(node)
      })
      // If title was set by us, we leave it as-is to avoid title flicker on SPA navigation.
    }
  }, [title, description, url, image, jsonLd])

  return null
}
